import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import type { AxiosError, AxiosResponse } from "axios"
import Cookies from "js-cookie"
import { useCallback } from "react"
import { useParams } from "react-router"
import { toast } from "react-toastify"

import type { Filters } from "@/constants/filters"
import { queryClient } from "@/queries/queries"
import type { ApiResponse } from "@/types"
import { useStore } from "@/zustand/store"

import { AUTH_COOKIE_NAME } from "@/constants/auth"
import useAxios from "./useAxios"

export interface GenericQueryParams {
  keys: Array<string>
  apiEndpoint: string
  filterName?: Filters
  paginated?: boolean
  enabled?: boolean
}

export function useGenericQuery<TData = unknown>(
  params: GenericQueryParams & { paginated: true }
): UseQueryResult<ApiResponse<TData>, Error>

export function useGenericQuery<TData = unknown>(
  params: GenericQueryParams & { paginated?: false }
): UseQueryResult<TData, Error>

export function useGenericQuery<TData = unknown>(
  params: GenericQueryParams
): UseQueryResult<TData | ApiResponse<TData>, Error>

export function useGenericQuery<TData = unknown>({
  keys,
  apiEndpoint,
  enabled,
  filterName,
  paginated,
}: GenericQueryParams) {
  const axios = useAxios()
  const authData = useStore((state) => state.authData)
  const setAuthData = useStore((state) => state.setAuthData)

  const filters = useStore((state) =>
    filterName ? state.filters[filterName] : {}
  )
  const { page: currPage } = useParams()

  const queryParams = new URLSearchParams(getFiltersQuery(filters))
  if (paginated) {
    queryParams.set("page", String(Number(currPage) || 1))
  }

  const link =
    apiEndpoint + (queryParams.toString() ? `?${queryParams.toString()}` : "")

  const messageError = useCallback(
    (err: AxiosError<ApiResponse<TData>>) => {
      if (!authData) toast.error(err.response?.data?.message)
      if (err.response?.status === 401) {
        setAuthData(undefined)
        Cookies.remove(AUTH_COOKIE_NAME)
        queryClient.clear()
      }
    },
    [authData, setAuthData]
  )

  const queryKey = [...keys]
  if (filterName) queryKey.push(String(filters))
  if (paginated) queryKey.push(String(currPage ? +currPage : 1))

  return useQuery({
    enabled: enabled,
    staleTime: Infinity,
    queryKey: queryKey,
    queryFn: async () => {
      const data: AxiosResponse<ApiResponse<TData>> = await axios.get(link)
      const returnedData = paginated ? data.data : data.data.data
      return returnedData
    },
    onError: messageError,
  })
}

function getFiltersQuery(filterObj: Record<string, unknown> = {}) {
  const filterQuery = new URLSearchParams()
  Object.entries(filterObj).forEach(([key, value]) => {
    if (value !== "") filterQuery.append(key, String(value))
  })
  return filterQuery.toString()
}
